import React, { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useCmsPage, pickTx } from "@/lib/pageCms";
import { usePageSEO } from "@/lib/seo";
import { RichText } from "@/components/RichText";

const INTERESTS = [
  { key: "event-day", da: "Løbsdag", en: "Event day", es: "Día del evento" },
  { key: "logistics", da: "Logistik", en: "Logistics", es: "Logística" },
  { key: "translation", da: "Oversættelse", en: "Translation", es: "Traducción" },
  { key: "story-telling", da: "Historiefortælling", en: "Storytelling", es: "Narrativa" },
  { key: "field-spain", da: "Feltarbejde i Spanien", en: "Field work in Spain", es: "Trabajo de campo en España" },
  { key: "fundraising", da: "Fundraising", en: "Fundraising", es: "Recaudación" },
];

export default function Volunteer() {
  const { t, lang } = useI18n();
  const cms = useCmsPage("volunteer");
  usePageSEO({}, {}, { cmsSeo: cms.seo });
  const hero = cms.enabled ? cms.sections.find(s => s.slug === "hero") : null;
  const formSec = cms.enabled ? cms.sections.find(s => s.slug === "form") : null;
  const heroEyebrow = pickTx(hero?.eyebrow, lang) || t("nav_volunteer");
  const heroHeadline = pickTx(hero?.headline, lang) || t("volunteer_title");
  const heroBody = pickTx(hero?.body, lang) || t("volunteer_sub");
  const successHeadline = pickTx(formSec?.success_headline, lang) || t("volunteer_ok");
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "", availability: "", message: "", interests: [] });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const toggle = (k) => setF(s => ({ ...s, interests: s.interests.includes(k) ? s.interests.filter(x => x !== k) : [...s.interests, k] }));
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/volunteers", { ...f, language: lang });
      setSent(true);
    } catch (ex) { setErr(ex.response?.data?.detail || "Fejl — prøv igen"); }
    finally { setBusy(false); }
  };
  const label = (i) => i[lang] || i.en;
  return (
    <div className="max-editorial container-x py-20 max-w-3xl">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <div className="label-mono text-terracotta">{heroEyebrow}</div>
        <h1 className="font-editorial text-5xl md:text-7xl mt-3 tracking-tighter">{heroHeadline}</h1>
        <div className="mt-4 text-lg text-ink/70 max-w-xl leading-relaxed">
          <RichText text={heroBody} gapClassName="mt-4" />
        </div>
      </motion.div>

      {sent ? (
        <div data-testid="volunteer-sent" className="mt-16 border border-line p-10">
          <Check size={28} className="text-terracotta" />
          <h2 className="font-editorial text-3xl mt-4">{successHeadline}</h2>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-12 space-y-8" data-testid="volunteer-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field required label={lang === "da" ? "Navn" : lang === "es" ? "Nombre" : "Name"} testid="v-name" v={f.name} on={v => setF({ ...f, name: v })} />
            <Field required type="email" label={t("email")} testid="v-email" v={f.email} on={v => setF({ ...f, email: v })} />
            <Field label={lang === "da" ? "Telefon" : lang === "es" ? "Teléfono" : "Phone"} testid="v-phone" v={f.phone} on={v => setF({ ...f, phone: v })} />
            <Field label={lang === "da" ? "By" : lang === "es" ? "Ciudad" : "City"} testid="v-city" v={f.city} on={v => setF({ ...f, city: v })} />
          </div>
          <div>
            <label className="label-mono text-ink/60">{t("volunteer_interests")}</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {INTERESTS.map(i => (
                <button type="button" key={i.key} data-testid={`v-int-${i.key}`}
                        onClick={() => toggle(i.key)}
                        className={`px-4 py-2 text-sm border transition-colors ${f.interests.includes(i.key) ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-line hover:border-ink"}`}>
                  {label(i)}
                </button>
              ))}
            </div>
          </div>
          <Field label={t("volunteer_availability")} testid="v-avail" v={f.availability} on={v => setF({ ...f, availability: v })} />
          <div>
            <label className="label-mono text-ink/60">{t("volunteer_message")}</label>
            <textarea data-testid="v-message" rows={5} value={f.message} onChange={e => setF({ ...f, message: e.target.value })}
                      className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
          </div>
          {err && <div className="text-sm text-red-600" data-testid="v-error">{err}</div>}
          <button disabled={busy} data-testid="v-submit"
                  className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-8 py-4 text-sm tracking-wide">
            {busy ? "…" : t("volunteer_send")}
          </button>
        </form>
      )}
    </div>
  );
}

function Field({ label, testid, v, on, type = "text", required }) {
  return (
    <div>
      <label className="label-mono text-ink/60">{label}</label>
      <input data-testid={testid} required={required} type={type} value={v}
             onChange={e => on(e.target.value)}
             className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
    </div>
  );
}
